/**
 * Versioned type definitions for screening audit log contexts
 *
 * These types are NOT auto-generated and should be preserved even when the API changes.
 * They represent the structure of audit log contexts at the time they were stored.
 *
 * IMPORTANT: Do NOT import auto-generated types from @nk/data-entities-lib here.
 * These types must remain stable to support rendering of audit logs from different versions.
 */

import { ScreeningCaseContext, SupplierCaseContextLegacy } from '../comply-advantage/models';

export interface ScreeningActionCompositeEntityV1 {
  uuid: string;
  screening_action: {
    uuid: string;
    screening_config_uuid: string;
    related_company_uuid: string;
    monitoring_person_uuid: string;
    monitoring_customer_person_uuid: string;
    monitoring_company_uuid: string;
    removed_flag: boolean;
    app_membership_uuid: string;
    monitoring_comment: string;
    monitoring_origin_key: string;
    monitoring_entity_uuid: string;
    monitoring_entity_key: string;
    entity_monitoring_type_uuid: string;
    entity_inquiry_uuid: string;
    entity_monitoring_reason_uuid: string;
    related_person_uuid: string;
    related_customer_person_uuid: string;
    related_entity_uuid: string;
    related_entity_key: string;
    approved_flag: boolean | null;
    decision_origin_key: string;
    decision_comment: string;
    monitoring_origin_entity_uuid: string;
    screening_entity_uuid: string;
    create_timestamp?: Date;
    relationship_start_date?: Date;
    decision_timestamp?: Date | null;
    app_url?: string;
  };
  monitoring_origin_entity: {
    uuid: string;
    screening_config_uuid: string;
    screening_entity_uuid: string;
    monitored_flag?: boolean;
    app_url?: string;
  } | null;
  monitoring_origin_entity_company: {
    uuid: string;
  } | null;
  monitoring_origin_entity_customer_person: {
    uuid: string;
    full_name: string;
    birth_date: Date | null;
    screening_config_uuid: string;
    gender_uuid: string;
    import_uuid: string;
    app_url?: string;
  } | null;
  screening_entity: {
    uuid: string;
    entity_uuid: string;
    entity_key: string;
    customer_person_uuid: string;
    app_url?: string;
  };
  screening_entity_company: {
    uuid: string;
  } | null;
  screening_entity_person: {
    uuid: string;
  } | null;
  screening_entity_customer_person: {
    uuid: string;
    full_name: string;
    birth_date: Date | null;
    screening_config_uuid: string;
    gender_uuid: string;
    import_uuid: string;
    app_url?: string;
  } | null;
}

export interface EntityMonitoringCompositeEntityV1 {
  uuid: string;
  related_person: {
    uuid: string;
    full_name: string;
    birth_date: string | null;
    gender_uuid: string | null;
    import_uuid: string | null;
    insert_timestamp: string;
    screening_config_uuid: string;
  } | null;
  related_company: {
    uuid: string;
    name: string;
    org_nr: string;
    org_nr_schema: string;
    insert_timestamp?: string;
    update_timestamp?: string;
  } | null;
  entity_monitoring: {
    uuid: string;
    removed_flag: number;
    create_timestamp: string;
    insert_timestamp: string;
    update_timestamp: string;
    monitoring_comment: string | null;
    related_entity_key: string;
    app_membership_uuid: string;
    entity_inquiry_uuid: string | null;
    related_entity_uuid: string;
    related_person_uuid: string | null;
    related_company_uuid: string | null;
    monitoring_entity_key: string;
    monitoring_origin_key: string;
    screening_config_uuid: string;
    monitoring_entity_uuid: string;
    monitoring_person_uuid: string | null;
    monitoring_company_uuid: string | null;
    relationship_start_date: string;
    entity_monitoring_type_uuid: string;
    related_customer_person_uuid: string;
    entity_monitoring_reason_uuid: string | null;
    monitoring_customer_person_uuid: string;
  };
  related_customer_person: {
    uuid: string;
    full_name: string;
    birth_date: string;
    gender_uuid: string;
    import_uuid: string | null;
    insert_timestamp: string;
    screening_config_uuid: string;
  } | null;
  entity_monitoring_decision: Array<{
    uuid: string;
    approved_flag: number;
    decision_comment: string;
    insert_timestamp: string;
    decision_timestamp: string;
    app_membership_uuid: string;
    decision_origin_key: string;
    entity_monitoring_uuid: string;
  }>;
  monitoring_person_composite: {
    uuid: string;
    person: Partial<{
      uuid: string;
      full_name: string;
      birth_date: Date | string | null;
      birth_year: number | null;
      birth_month: number | null;
      birth_day: number | null;
      gender_uuid: string | null;
      import_uuid: string | null;
      insert_timestamp: string;
      screening_config_uuid: string;
      national_identification_number: string;
      national_identification_schema: string;
    }>;
    person_locations: unknown[];
    person_national_identifications: unknown[];
  } | null;
  monitoring_company_composite: {
    uuid: string;
    company: {
      uuid: string;
      name: string;
      org_nr: string;
      org_nr_schema: string;
      insert_timestamp?: string;
      update_timestamp?: string;
    };
  } | null;
  monitoring_customer_person_composite: {
    uuid: string;
    customer_person: Partial<{
      uuid: string;
      full_name: string;
      birth_date: Date | string | null;
      gender_uuid: string;
      import_uuid: string | null;
      insert_timestamp: string;
      screening_config_uuid: string;
    }>;
    customer_person_locations: unknown[];
    customer_person_national_identifications: unknown[];
  } | null;
}

export interface ScreeningCaseReviewEntityV1 {
  screening_case_uuid: string;
  reviewer_app_membership_uuid: string;
  screening_case_review_status_uuid: string;
  entity_match_decision_type_uuid: string;
  screening_case_review_comment: string;
  save_timestamp: Date | null;
  complete_timestamp: Date | null;
  pep_confirmed_flag: boolean | null;
  uuid: string;
  created_timestamp?: Date;
}

export enum ScreeningCaseReviewStatusKeyV1 {
  complete = 'complete',
  ongoing = 'ongoing',
  pending = 'pending',
}

export interface ScreeningCaseReviewStatusEntityV1 {
  screening_case_review_status_key: ScreeningCaseReviewStatusKeyV1;
  uuid: string;
  app_url?: string;
}

export enum EntityMatchDecisionTypeKeyV1 {
  bad_match = 'bad_match',
  bad_match_dont_match_again = 'bad_match_dont_match_again',
  good_match = 'good_match',
}

export interface EntityMatchDecisionTypeEntityV1 {
  entity_match_decision_type_key: EntityMatchDecisionTypeKeyV1;
  uuid: string;
  app_url?: string;
}

export interface ScreeningCaseReviewCompositeEntityV1 {
  screening_case_review: ScreeningCaseReviewEntityV1;
  screening_case_review_status: ScreeningCaseReviewStatusEntityV1;
  entity_match_decision_type: EntityMatchDecisionTypeEntityV1 | null;
}

/**
 * V1.0 structure for ScreeningCaseCompositeEntity
 * This represents the structure as it existed when V1.0 logs were created.
 * DO NOT change this structure - it must match what's stored in the database.
 */
export interface ScreeningCaseCompositeEntityV1 {
  entity_inquiry: {
    uuid: string;
    monitored_flag: number;
    insert_timestamp: string;
    update_timestamp: string;
    grid_inquiry_uuid: string;
    inquiry_entity_key: string;
    inquiry_entity_uuid: string;
    inquiry_person_uuid: string | null;
    inquiry_company_uuid: string | null;
    screening_config_uuid: string;
    inquiry_customer_person_uuid: string | null;
  };
  screening_case: {
    uuid: string;
    pep_flag: number;
    sanction_flag: number;
    grid_alert_uuid: string | null;
    insert_timestamp: string;
    update_timestamp: string;
    created_timestamp: string;
    entity_inquiry_uuid: string;
    screening_config_uuid: string;
    screening_case_report_uuid: string | null;
    assignee_app_membership_uuid: string | null;
  };
  monitoring_person: {
    uuid: string;
    birth_day?: number;
    full_name: string;
    birth_date: string;
    birth_year?: number;
    birth_month?: number;
    gender_uuid: string;
    postal_code?: string;
    street_name?: string | null;
    country_name?: string;
    postal_place?: string;
    street_letter?: string | null;
    street_number?: string | null;
    surrogate_key?: string;
    surrugate_key?: number;
    street_address?: string;
    country_code_two?: string;
    insert_timestamp?: string;
    update_timestamp?: string;
    disambiguate_uuid?: string;
    municipality_code?: string;
    municipality_name?: string;
    person_master_uuid?: string;
    composite_business_key?: string;
    person_location_type_key?: string | null;
    national_identification_number?: string;
    national_identification_schema?: string;
  } | null;
  related_companies: Array<{
    name: string;
    uuid: string;
    org_nr?: string;
    org_nr_schema?: string;
    insert_timestamp?: string;
    update_timestamp?: string;
  }>;
  monitoring_company: {
    uuid: string;
    name?: string;
    org_nr?: string;
    org_nr_schema?: string;
    insert_timestamp?: string;
    update_timestamp?: string;
  } | null;
  screening_state_aggregate: {
    pep_flag: boolean;
    sanction_flag: boolean;
    pep_confirmed_flag: boolean;
    entity_match_decision_type_keys: string[];
    screening_case_review_status_keys: string[];
    screening_case_latest_update_timestamp: string;
  };
  monitoring_customer_person: {
    uuid: string;
    full_name: string;
    birth_date: string | null;
    gender_uuid: string | null;
    import_uuid: string | null;
    insert_timestamp: string;
    screening_config_uuid: string;
  } | null;
  screening_case_review_composites: ScreeningCaseReviewCompositeEntityV1[];
}

/**
 * V2.0 structure for ScreeningCaseCompositeEntity
 * This represents the structure as it exists in V2.0 logs.
 * Note: V2.0 has additional nested structures (like screening_context)
 */
export interface ScreeningCaseCompositeEntityV2 {
  person: {
    uuid: string;
  } | null;
  company: {
    uuid: string;
  } | null;
  screening_case: {
    uuid: string;
    pep_flag: number;
    adverse_flag: number;
    sanction_flag: number;
    grid_alert_uuid: string | null;
    insert_timestamp: string;
    update_timestamp: string;
    created_timestamp: string;
    screening_context?: SupplierCaseContextLegacy;
    entity_inquiry_uuid: string | null;
    screening_config_uuid: string;
    screening_entity_uuid: string;
    external_case_identifier: string | null;
    screening_case_report_uuid: string | null;
    assignee_app_membership_uuid: string | null;
  };
  customer_person: {
    uuid: string;
    full_name: string;
    birth_date: string | null;
    gender_uuid: string | null;
    import_uuid: string | null;
    insert_timestamp: string;
    screening_config_uuid: string;
  } | null;
  screening_entity: {
    uuid: string;
    entity_key: string;
    entity_uuid: string | null;
    insert_timestamp: string;
    update_timestamp: string;
    customer_person_uuid: string | null;
  };
  monitoring_connection: {
    uuid: string;
    insert_timestamp: string;
    screening_config_uuid: string;
    screening_entity_uuid: string;
    monitoring_origin_entity_uuid: string;
  } | null;
  screening_case_review_composites: Array<{
    screening_case_review: {
      uuid: string;
      save_timestamp: string | null;
      insert_timestamp: string;
      update_timestamp: string;
      created_timestamp: string;
      complete_timestamp: string | null;
      pep_confirmed_flag: number | null;
      screening_case_uuid: string;
      reviewer_app_membership_uuid: string;
      screening_case_review_comment: string | null;
      entity_match_decision_type_uuid: string | null;
      screening_case_review_status_uuid: string;
    };
    entity_match_decision_type: {
      uuid: string;
      insert_timestamp: string;
      entity_match_decision_type_key: string;
    } | null;
    screening_case_review_status: {
      uuid: string;
      insert_timestamp: string;
      screening_case_review_status_key: string;
    };
  }>;
  monitoring_connection_screening_case_state: {
    uuid: string;
    pep_flag: number;
    sanction_flag: number;
    pep_declined_flag: number;
    possible_pep_flag: number;
    screening_case_uuid: string;
    possible_sanction_flag: number;
    pep_match_declined_flag: number;
    sanction_match_declined_flag: number;
    screening_case_state_update_timestamp: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    screening_action__screening_config_uuid: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    screening_action__monitoring_origin_entity_uuid: string;
  };
}

/**
 * V1.0 structure for ScreeningConfigEntity
 * This represents the structure as it existed when V1.0 logs were created.
 * DO NOT change this structure - it must match what's stored in the database.
 */
export interface ScreeningConfigEntityV1 {
  uuid: string;
  insert_timestamp: string;
  update_timestamp: string;
  app_customer_uuid: string;
  auto_approve_monitoring_flag: number;
}

/**
 * V2.0 structure for ScreeningConfigEntity
 * This represents the structure as it exists in V2.0 logs.
 * V2.0 has additional fields compared to V1.0.
 */
export interface ScreeningConfigEntityV2 {
  uuid: string;
  insert_timestamp: string;
  update_timestamp: string;
  app_customer_uuid: string;
  auto_approve_monitoring_flag: number;
  subscribe_to_email_notifications_flag: number;
  screening_config_system_suggestion_setting_uuid: string;
}

/**
 * V3.0 structure for ScreeningCaseCompositeEntity
 * This represents the structure as it exists in V3.0 logs.
 * Note: V3.0 introduces separate screening_case_state and screening_entity_screening_case_state
 * replacing the previous monitoring_connection_screening_case_state structure.
 */
export interface ScreeningCaseCompositeEntityV3 {
  screening_case: {
    uuid: string;
    grid_alert_uuid: string | null;
    entity_inquiry_uuid: string | null;
    assignee_app_membership_uuid: string | null;
    screening_config_uuid: string;
    created_timestamp: string;
    screening_case_report_uuid: string | null;
    pep_flag: boolean;
    sanction_flag: boolean;
    screening_entity_uuid: string;
    screening_context: ScreeningCaseContext | null;
    adverse_flag: boolean;
    external_case_identifier: string | null;
    app_url: string | null;
  };
  screening_case_state: {
    uuid: string;
    screening_config_uuid: string;
    screening_case_uuid: string;
    screening_entity_uuid: string;
    pep_flag: boolean;
    sanction_flag: boolean;
    sanction_match_declined_flag: boolean;
    possible_pep_flag: boolean;
    possible_sanction_flag: boolean;
    pep_declined_flag: boolean;
    pep_match_declined_flag: boolean;
    latest_screening_case_review_update_timestamp: string;
    app_url: string | null;
  };
  screening_entity: {
    uuid: string;
    entity_uuid: string;
    entity_key: string;
    customer_person_uuid: string | null;
    app_url: string | null;
  };
  screening_entity_screening_case_state: {
    uuid: string;
    screening_config_uuid: string;
    screening_entity_uuid: string;
    pep_flag: boolean;
    sanction_flag: boolean;
    sanction_match_declined_flag: boolean;
    possible_pep_flag: boolean;
    possible_sanction_flag: boolean;
    pep_declined_flag: boolean;
    pep_match_declined_flag: boolean;
    screening_case_state_update_timestamp: string;
    app_url: string | null;
  };
  monitoring_connection: {
    uuid: string;
    screening_config_uuid: string;
    monitoring_origin_entity_uuid: string;
    screening_entity_uuid: string;
    app_url: string | null;
  } | null;
  person: {
    uuid: string;
    person_master_uuid: string;
    full_name: string;
    birth_date: string;
    birth_year: number;
    birth_month: number;
    birth_day: number;
    national_identification_number: string;
    national_identification_schema: string;
    surrogate_key: string;
    composite_business_key: string;
    street_address: string | null;
    postal_code: string | null;
    postal_place: string | null;
    municipality_code: string | null;
    municipality_name: string | null;
    country_code_two: string | null;
    country_name: string | null;
    street_name: string | null;
    street_number: string | null;
    street_letter: string | null;
    person_location_type_key: string | null;
    gender_uuid: string;
    data_origin_ids: string;
    app_url: string | null;
  } | null;
  customer_person: {
    uuid: string;
    full_name: string;
    birth_date: string | null;
    gender_uuid: string | null;
    import_uuid: string | null;
    insert_timestamp: string;
    screening_config_uuid: string;
    app_url: string | null;
  } | null;
  company: {
    uuid: string;
    name: string;
    org_nr: string;
    org_nr_schema: string;
    insert_timestamp?: string;
    update_timestamp?: string;
    app_url: string | null;
  } | null;
  screening_case_review_composites: Array<{
    entity_match_decision_type: {
      uuid: string;
      entity_match_decision_type_key: string;
      app_url: string | null;
    } | null;
    screening_case_review_status: {
      uuid: string;
      screening_case_review_status_key: string;
      app_url: string | null;
    };
    screening_case_review: {
      uuid: string;
      screening_case_uuid: string;
      reviewer_app_membership_uuid: string;
      screening_case_review_status_uuid: string;
      entity_match_decision_type_uuid: string | null;
      created_timestamp: string;
      screening_case_review_comment: string | null;
      save_timestamp: string | null;
      complete_timestamp: string | null;
      pep_confirmed_flag: boolean | null;
      app_url: string | null;
    };
  }>;
}
